import { useState } from "react";
import { Pencil } from "lucide-react";
import { ShieldAlert, Sparkles } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import type { CallNextStep, CallStepTask } from "../../../lib/calls";
import { IconBtn } from "../../ui";
import { CallStepRow } from "./CallStepRow";
import { CallTextEdit } from "./CallTextEdit";

/**
 * What the model made of the finished call: the summary, and under it the
 * concrete steps it concluded should follow.
 *
 * The steps are checkboxes and they save. That is the whole point of the block:
 * a list of actions that does not remember which ones were taken is a decoration
 * that costs the reader more attention than it returns, because it looks the
 * same on the second visit as it did on the first.
 *
 * The tick is optimistic - it lands before the request does. Nothing else on
 * this pane depends on the flag, so the cheap failure (a tick that flicks back
 * on a lost request) is much better than the expensive one (a checkbox that sits
 * there unmoved for a round trip and gets clicked again).
 */
export function CallSummary({
  summary,
  objections,
  steps,
  stepTasks,
  canMakeTask,
  editing,
  onEditing,
  editedAt,
  editedBy,
  onSaveSummary,
  onSteps,
  onTask,
}: {
  summary: string | null;
  /**
   * What the customer pushed back on. Empty on most calls and on every
   * voicemail, and empty is drawn as nothing rather than as "no objections":
   * the model not finding one and the customer not raising one are the same
   * value here, and only one of them is worth a sentence.
   */
  objections: string[];
  steps: CallNextStep[] | null;
  /** The live state of the tasks some of these steps were filed as. */
  stepTasks?: CallStepTask[];
  /** Filing a task needs manage:crm.projects on top of the calls permission. */
  canMakeTask: boolean;
  editing: boolean;
  onEditing: (v: boolean) => void;
  /** Already formatted by the pane, which owns the date helpers. */
  editedAt?: string | null;
  editedBy?: string | null;
  /** Absent when this viewer may see the call but not hear it, in which case
   *  there is nothing to correct and no pencil is drawn. */
  onSaveSummary?: (value: string | null) => Promise<void>;
  onSteps: (steps: CallNextStep[]) => Promise<void>;
  onTask: (text: string, dueDate: string | null) => Promise<void>;
}) {
  const t = useT();
  // Only while a write is in flight. Otherwise the props are the truth, so a
  // call switched underneath this component cannot show the previous one's ticks.
  const [pending, setPending] = useState<CallNextStep[] | null>(null);

  const stored = steps ?? [];
  const shown = pending ?? stored;
  const byId = new Map((stepTasks ?? []).map((task) => [task.id, task]));

  // An empty summary is still worth the box once it can be WRITTEN: correcting
  // a model that produced nothing is the same act as correcting one that
  // produced the wrong thing. Objections count towards the box being worth
  // drawing at all: a call with nothing else read off it but one recorded
  // blocker is exactly the row somebody is looking for.
  if (!summary && shown.length === 0 && objections.length === 0 && !onSaveSummary) return null;

  async function toggle(text: string) {
    const next = shown.map((step) => (step.text === text ? { ...step, done: !step.done } : step));
    setPending(next);
    try {
      await onSteps(next);
    } finally {
      // Either way the props now carry the server's answer, which is the one
      // worth drawing - including when it disagrees with the click.
      setPending(null);
    }
  }

  return (
    <div className="crm-ai-box">
      <div className="crm-ai-t">
        <Sparkles className="size-4" />
        {t("calls.detail.summary")}
        {onSaveSummary && !editing ? (
          <span className="crm-push">
            <IconBtn small onClick={() => onEditing(true)} title={t("calls.detail.editSummary")}>
              <Pencil className="size-3.5" />
            </IconBtn>
          </span>
        ) : null}
      </div>
      {editing && onSaveSummary ? (
        <CallTextEdit
          value={summary}
          editing={editing}
          onEditing={onEditing}
          onSave={onSaveSummary}
          placeholder={t("calls.detail.summaryPlaceholder")}
          rows={5}
        />
      ) : summary ? (
        <p className="crm-ai-p">{summary}</p>
      ) : null}
      {/* Said once, under the summary, rather than beside each corrected half:
          the point is that a human has been over this call, which is a fact
          about the record and not about one paragraph of it. */}
      {!editing && editedAt ? (
        <p className="crm-ai-by">{t("calls.detail.corrected", { who: editedBy ?? "?", at: editedAt })}</p>
      ) : null}

      {/* Above the next steps, because a blocker is why a step exists. Read in
          the other order the list of actions arrives before the reason for it,
          and the reason is the part that generalises across calls. */}
      {objections.length ? (
        <div className="crm-ai-obj">
          <div className="crm-ai-next-t">
            <ShieldAlert className="mr-1 inline size-3.5 align-[-2px]" />
            {t("calls.detail.objections")}
          </div>
          <div className="crm-obj-list">
            {objections.map((objection) => (
              <span key={objection} className="crm-obj">
                {objection}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {shown.length ? (
        <div className="crm-ai-next">
          <div className="crm-ai-next-t">{t("calls.detail.nextSteps")}</div>
          {shown.map((step) => (
            <CallStepRow
              key={step.text}
              step={step}
              task={step.taskId ? byId.get(step.taskId) : undefined}
              canMakeTask={canMakeTask}
              onToggle={toggle}
              onTask={onTask}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
